import GtmDataExtractor from '@theme/gtm-data-extractor';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';

/** @typedef {{ name: string, value: string }} CartOption */
/** @typedef {{ variant_id: number, quantity: number, options_with_values?: CartOption[] }} CartItem */
/** @typedef {{ items?: CartItem[], items_added?: CartItem[], items_removed?: CartItem[] }} CartResource */

// ANCHOR PAGEVIEW (INIT GA4)
GtmDataExtractor.gtmPush({ event: 'page_viewed' }).then(() => {
  // ANCHOR VIEW_ITEM
  if (window.THEME_INFO.pageType == 'product') {
    GtmDataExtractor.sendViewEvent('gtm-view-item', {
      event: 'view_item',
      method: 'load',
    });
    GtmDataExtractor.sendViewEvent('gtm-view-item-update', {
      event: 'selected_variant',
      method: 'selected',
    });
    document.addEventListener(ThemeEvents.variantUpdate, function (evt) {
      GtmDataExtractor.sendViewEvent('gtm-view-item-update', {
        event: 'selected_variant',
        method: 'selected',
      });
    });
  }

  // ANCHOR VIEW_ITEM_LIST
  const pageType = window.THEME_INFO.pageType;
  if (pageType == 'collection' || pageType == 'search') {
    const isSearch = pageType == 'search';
    const containerClass = isSearch ? 'gtm-search' : 'gtm-collections';
    // Search has no collection to take the list name from
    /** @type {Record<string, string>} */
    const listInfo = isSearch ? { listname: 'search_results', listid: 'search_results' } : {};

    /**
     * @param {string} method
     * @param {Record<string, string | number | null>[]} [alreadyExtractedData]
     */
    const sendListing = (method, alreadyExtractedData) => {
      const items = (alreadyExtractedData ?? GtmDataExtractor.extractData(containerClass)).map((item) => ({
        ...item,
        ...listInfo,
      }));
      // An empty search has nothing to list
      if (isSearch && items.length === 0) return;

      GtmDataExtractor.sendViewEvent(containerClass, { event: 'view_item_list', method }, items);
    };

    sendListing('load');
    document.addEventListener(ThemeEvents.FilterUpdate, function () {
      sendListing('refresh');
    });
    document.addEventListener(ThemeEvents.paginatedListPageLoaded, function (evt) {
      const items = /** @type {CustomEvent<{ items: Element[] }>} */ (evt).detail?.items ?? [];
      const extractedItems = GtmDataExtractor.extractDataFromElements(items);
      if (extractedItems.length === 0) return;

      sendListing('load_more', extractedItems);
    });
  }
  trackListImpressions();

  // ANCHOR SELECT_ITEM
  trackSelectItem();

  // ANCHOR CHECKOUT ITEMS DATA
  trackCheckoutItemsData();

  // ANCHOR CART_EVENTs
  document.addEventListener(ThemeEvents.cartUpdate, function (evt) {
    const cartEvt = /** @type {CartUpdateEvent} */ (evt);
    const detail = cartEvt.detail;
    if (!detail || detail.data?.didError) return;

    const cartResource = /** @type {CartResource | undefined} */ (detail.resource);
    if (!cartResource) return;

    const itemsRemoved = cartResource.items_removed ?? [];
    const itemsAdded = cartResource.items_added ?? [];

    let eventType = '';
    /** @type {CartItem[]} */
    if (itemsRemoved.length > 0) {
      eventType = 'remove_from_cart';
    } else if (itemsAdded.length > 0) {
      eventType = 'add_to_cart';
    } else {
      return;
    }
    const method = window.THEME_INFO.pageType == 'cart' ? 'cart_page' : 'sidebar';

    const changedRows = detail.data?.oldRows ?? [];
    changedRows.forEach((row) => {
      const gtmVariantId = row.dataset.gtmVariantid;
      if (!gtmVariantId) return;

      const changedItems = eventType === 'add_to_cart' ? itemsAdded : itemsRemoved;
      const changedItem = changedItems.find((item) => item.variant_id.toString() === gtmVariantId);
      const quantity = changedItem?.quantity || 0;
      const extractedData = GtmDataExtractor.getDataFromElement(row, { quantity });
      GtmDataExtractor.sendViewEvent(eventType, { event: eventType, method: method }, extractedData);
    });
  });

  document.addEventListener(CartAddEvent.eventName, function (evt) {
    const cartEvt = /** @type {CartAddEvent} */ (evt);
    const detail = cartEvt.detail;
    if (!detail || detail.data?.didError) return;

    const cartResource = /** @type {CartResource | undefined} */ (detail.resource);
    if (!cartResource) return;
    const { items_removed, items_added } = cartResource;
    if ((items_removed?.length ?? 0) > 0 || (items_added?.length ?? 0) > 0) return;

    const method = detail.data?.source === 'quick-add-inline-component' ? 'quick_shop' : 'pdp';
    const variantId = detail.sourceId || detail.data?.variantId;
    if (!variantId) return;

    const itemInCart = cartResource.items?.find((item) => item.variant_id.toString() === variantId);
    if (!itemInCart) return;

    const quantity = itemInCart.quantity;
    /** @type {Record<string, string | number>} */
    const overrideData = {
      quantity: quantity || 1,
      variantid: variantId,
    };

    // Quick shop buttons carry the data of their own variant, so look there first (the card only has one variant)
    const searchScope = method === 'quick_shop' && cartEvt.target instanceof Element ? cartEvt.target : document;
    let productAdded =
      searchScope.querySelector(`[data-gtm-variantid="${variantId}"]`) ??
      document.querySelector(`[data-gtm-variantid="${variantId}"]`);
    if (!productAdded) {
      const productId = detail.data?.productId;
      productAdded = document.querySelector(`[data-gtm-productid="${productId}"]`);
      // The element found is for another variant and the cart option is translated, so the English size is unknown
      overrideData.size = 'data_not_set';
      overrideData.discount_percent = 'data_not_set';
      overrideData.available = 'data_not_set';
    }

    if (!(productAdded instanceof HTMLElement)) return;

    const extractedData = GtmDataExtractor.getDataFromElement(productAdded, overrideData);
    // Only the PDP form provides it (standard or sticky button)
    const type = detail.data?.type;
    GtmDataExtractor.sendViewEvent(
      'add_to_cart',
      { event: 'add_to_cart', method: method, ...(type && { type }) },
      extractedData,
    );
  });
  if (window.THEME_INFO.pageType == 'search') {
    GtmDataExtractor.sendViewEvent('gtm-search', {
      event: 'search',
      method: 'load',
    });
  }
});

/**
 * Sends `select_item` when a product card (listing, carousels, search suggestions, hero showcase...) is clicked in a
 * way that leads to the product page. Quick add, swatches and the other controls inside the card are ignored.
 */
function trackSelectItem() {
  // Allowed fields for the item in the `select_item` event
  const itemFields = [
    'selectedvariant',
    'meta',
    'price',
    'compare',
    'omnibus',
    'sku',
    'productid',
    'variantid',
    'size',
    'discount_percent',
    'available',
  ];

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const card = event.target.closest('product-card, .variant-image-showcase');
    if (!(card instanceof HTMLElement) || card.dataset.isGtmItem !== 'true') return;
    // Placeholders have no variant and no-navigation cards open a modal instead of the product page
    if (!card.dataset.gtmVariantid || card.hasAttribute('data-no-navigation')) return;

    // Same controls ProductCard.navigateToProduct ignores, plus swatches: they act on the card, not open the product
    const control = event.target.closest('button, input, label, select, [tabindex="1"], kway-swatches');
    if (control && card.contains(control)) return;

    // The showcase image is only navigable through its own link, product cards also navigate from any other spot
    if (card.matches('.variant-image-showcase') && !event.target.closest('a[href]')) return;

    const data = GtmDataExtractor.getDataFromElement(card);
    const item = Object.fromEntries(itemFields.map((field) => [field, data[field] ?? null]));
    GtmDataExtractor.sendViewEvent('gtm-select-item', { event: 'select_item', method: 'load' }, item);
  });
}

/**
 * The checkout pixel only gets standard Shopify data (no compare at price, no stock), so discount and availability of
 * the cart rows and of the product page variant (buy now) are saved in localStorage by variant id for the pixel to read.
 */
function trackCheckoutItemsData() {
  const storageKey = 'gtm_checkout_items';
  const maxItems = 50;
  const itemsSelector = '.cart-items__table-row[data-is-gtm-item], .gtm-view-item-update[data-is-gtm-item]';

  /** @param {ParentNode} root */
  const saveItems = (root) => {
    const newItems = Array.from(root.querySelectorAll(itemsSelector))
      .filter((element) => element instanceof HTMLElement)
      .map((element) => GtmDataExtractor.getDataFromElement(/** @type {HTMLElement} */ (element)))
      .filter((data) => data.variantid)
      .map((data) => ({
        variantid: String(data.variantid),
        discount_percent: data.discount_percent ?? 'data_not_set',
        available: data.available ?? 'data_not_set',
      }));
    if (newItems.length === 0) return;

    try {
      const storedItems = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const newIds = newItems.map((item) => item.variantid);
      // Newest last, so the oldest ones are dropped first
      const items = [
        ...(Array.isArray(storedItems) ? storedItems : []).filter((item) => !newIds.includes(item.variantid)),
        ...newItems,
      ].slice(-maxItems);
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch (error) {
      // Storage full or disabled, the pixel will send data_not_set
    }
  };

  saveItems(document);

  document.addEventListener(ThemeEvents.variantUpdate, () => saveItems(document));

  // Fired before the cart is morphed, so the rows are read from the new sections HTML
  document.addEventListener(ThemeEvents.cartUpdate, (evt) => {
    const sections = /** @type {CartUpdateEvent} */ (evt).detail?.data?.sections;
    if (!sections) return;

    Object.values(sections).forEach((html) => {
      if (typeof html !== 'string') return;
      saveItems(new DOMParser().parseFromString(html, 'text/html'));
    });
  });
}

/**
 * Sends `view_item_list` once per `[data-gtm-list]` container (grids and carousels outside the listing pages), the
 * first time it is on screen with products inside. Lists loaded later (recommendations) are caught by the mutation
 * observer.
 */
function trackListImpressions() {
  /** @type {Set<Element>} */
  const visibleLists = new Set();
  /** @type {Set<Element>} */
  const sentLists = new Set();
  /** @type {Map<Element, MutationObserver>} */
  const mutationObservers = new Map();

  /** @param {Element} list */
  const sendIfReady = (list) => {
    if (sentLists.has(list) || !visibleLists.has(list)) return;
    if (!(list instanceof HTMLElement)) return;

    const items = GtmDataExtractor.extractListItems(list, {
      listname: list.dataset.gtmList ?? '',
      listid: list.dataset.gtmListId ?? '',
    });
    if (items.length === 0) return;

    sentLists.add(list);
    intersectionObserver.unobserve(list);
    mutationObservers.get(list)?.disconnect();
    GtmDataExtractor.sendViewEvent('gtm-list', { event: 'view_item_list', method: 'visible' }, items);
  };

  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0.1) {
          visibleLists.add(entry.target);
        } else {
          visibleLists.delete(entry.target);
        }
        sendIfReady(entry.target);
      });
    },
    { threshold: [0, 0.2] },
  );

  document.querySelectorAll('[data-gtm-list]').forEach((list) => {
    const mutationObserver = new MutationObserver(() => sendIfReady(list));
    mutationObserver.observe(list, { childList: true, subtree: true });
    mutationObservers.set(list, mutationObserver);
    intersectionObserver.observe(list);
  });
}
